/**
 * SPEC-TrainingSimulator §8.2 / §8.3 (Phases 3–4) — the real decision footer
 * and the real resolution panel, in the trainee runner.
 *
 * Both live components are mounted **unchanged** from `components/qa/*`: the
 * whole point of D1 is that the screen a trainee certifies on is the screen
 * they will work on. They are prop-driven and issue no requests of their own,
 * so the only wiring here is where their callbacks point:
 *
 *   Good              → onDecide("GOOD")            → POST /attempts/…/decision
 *   Bad               → opens AgentResolutionPanel  (no request yet)
 *   panel Save        → onDecide("BAD", …)          → POST /attempts/…/decision
 *   panel Undo        → back to the footer
 *   CX Notified, Skip → inert (§8.2), like Add flag on the clone
 *
 * Nothing here ever calls `/api/qa/*` (§8.5). CX Notified and Skip keep their
 * live look so the layout is honest, but a training run has no customer to
 * notify and no queue to skip into, so they are click-blocked the same way the
 * clone's outbound links are: `pointer-events: none`, a capture-phase guard,
 * out of the tab order, `aria-disabled`, and the shared training tooltip.
 */

import { useEffect, useRef } from "react";
import { DecisionFooter } from "@/components/qa/decision-footer";
import { AgentResolutionPanel } from "@/components/qa/agent-resolution-panel";
import type { BadReasonUi, InitialActionUi } from "@/lib/agent-ui-helpers";

/** The panel's Save payload — its own prop type, so a change there is a typecheck error here. */
export type ResolutionSave = {
  badReason: BadReasonUi;
  badReasonOther?: string;
  /** Never present in training: Step 2 is hidden (`hideInitialAction`, 2026-09-15). */
  initialAction?: InitialActionUi;
  initialActionOther?: string;
  initialNote?: string;
};

export interface RunDecisionFooterProps {
  customer: string | null | undefined;
  /** Bad opens the panel; the trainee can Undo back to the footer. */
  badOpen: boolean;
  onBadOpenChange: (open: boolean) => void;
  onGood: () => void;
  onSaveBad: (input: ResolutionSave) => void;
  saving?: boolean;
}

// `!important` on every declaration: both live buttons set `opacity` inline
// (their in-flight state), and an inline style beats a plain stylesheet rule —
// without it they stay fully coloured and do not read as unavailable.
const INERT_CSS = `
[data-inert-controls="true"] [data-testid="agent-detail-notify-cu-toggle"],
[data-inert-controls="true"] [data-testid="agent-detail-decision-skip"]{
  pointer-events:none !important;cursor:not-allowed !important;opacity:.5 !important;filter:grayscale(1) !important
}
`;

const INERT_IDS = ["agent-detail-notify-cu-toggle", "agent-detail-decision-skip"] as const;

/** The footer owns its markup, so the disabled semantics are applied from out here after every render. */
function useInertControls(ref: React.RefObject<HTMLDivElement>) {
  useEffect(() => {
    for (const id of INERT_IDS) {
      const el = ref.current?.querySelector<HTMLElement>(`[data-testid="${id}"]`);
      if (!el) continue;
      el.tabIndex = -1;
      el.setAttribute("aria-disabled", "true");
      el.setAttribute("title", "Not available in training");
    }
  });
}

export function RunDecisionFooter({ customer, badOpen, onBadOpenChange, onGood, onSaveBad, saving }: RunDecisionFooterProps) {
  const ref = useRef<HTMLDivElement>(null);
  useInertControls(ref);
  return (
    <div
      ref={ref}
      data-testid="run-decision-footer"
      data-inert-controls="true"
      onClickCapture={(e) => {
        const el = (e.target as HTMLElement | null)?.closest?.(INERT_IDS.map((i) => `[data-testid="${i}"]`).join(","));
        if (el) { e.preventDefault(); e.stopPropagation(); }
      }}
      style={{
        position: "sticky",
        bottom: 0,
        padding: "12px 0",
        background: "linear-gradient(180deg, transparent, #F7F8FA 30%)",
      }}
    >
      <style data-testid="run-footer-inert">{INERT_CSS}</style>
      {badOpen ? (
        <AgentResolutionPanel
          onSave={onSaveBad}
          onUndo={() => onBadOpenChange(false)}
          saving={saving}
          hideInitialAction
          requireNote
        />
      ) : (
        <DecisionFooter
          viewMode="agent"
          customer={customer}
          // Training content is CookUnity (QATT_1), so the inert button reads "CU Notified".
          expectedQatt={1}
          onGood={onGood}
          onBad={() => onBadOpenChange(true)}
          onSkip={() => { /* inert in training (§8.2) */ }}
          notifyOn={false}
          onNotifyChange={() => { /* inert in training (§8.2) */ }}
          decisionInFlight={saving}
        />
      )}
    </div>
  );
}

export default RunDecisionFooter;
